import { Suspense, lazy } from "react";
import { Toaster } from "@/components/molecules/toaster";
import { Toaster as Sonner } from "@/components/molecules/sonner";
import { TooltipProvider } from "@/components/molecules/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import { LanguageProvider } from "@/context/LanguageProvider";
import { ProtectedRoute } from "@/components/templates/ProtectedRoute";
import { RoleProtectedRoute } from "@/components/templates/RoleProtectedRoute";
import { DashboardLayout } from "@/components/templates/DashboardLayout";
import { ErrorBoundary } from "@/components/templates/ErrorBoundary";
import { LoadingSpinner } from "@/components/atoms/LoadingSpinner";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Medical = lazy(() => import("./pages/medical"));
const AnalysisMedical = lazy(() => import("./pages/AnalysisMedical"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const NotFound = lazy(() => import("./pages/NotFound"));
// Admin pages
const Settings = lazy(() => import("./pages/admin/Settings"));
const Overview = lazy(() => import("./pages/admin/Overview"));
const AIDiagnostics = lazy(() => import("./pages/admin/AIDiagnostics"));
const EmergencyAlerts = lazy(() => import("./pages/EmergencyAlerts"));
// Doctor pages
const PatientRecord = lazy(() => import("./pages/doctor/PatientRecord"));
const Prescriptions = lazy(() => import("./pages/doctor/Prescriptions"));
const CaseReviewQueue = lazy(() => import("./pages/doctor/CaseReviewQueue"));
const PatientQueuePage = lazy(() => import("./pages/doctor/PatientQueuePage"));
// Nurse pages
const NurseDashboard = lazy(() => import("./pages/nurse/NurseDashboard"));
const VitalSigns = lazy(() => import("./pages/nurse/VitalSigns"));
const NurseNotes = lazy(() => import("./pages/nurse/NurseNotes"));
const PatientStatus = lazy(() => import("./pages/nurse/PatientStatus"));
// Multi-tenancy admin pages
const Invitations = lazy(() => import("./pages/admin/Invitations"));
const TenantSettings = lazy(() => import("./pages/admin/TenantSettings"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <TenantProvider>
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner fullScreen />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/accept-invitation" element={<AcceptInvitation />} />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <DashboardLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<Dashboard />} />
                      {/* Medical Dashboard - Admin and Doctor only */}
                      <Route
                        path="medical"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "doctor"]}>
                            <Medical />
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Medical Analysis - Admin and Doctor only (nurses use their own dashboard) */}
                      <Route
                        path="analysis/:patientId?"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "doctor"]}>
                            <AnalysisMedical />
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Members (user management) - Admin only */}
                      <Route
                        path="members"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin"]}>
                            <UserManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Admin Pages */}
                      <Route
                        path="admin/overview"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin"]}>
                            <Overview />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="admin/ai-diagnostics"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin"]}>
                            <AIDiagnostics />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="emergency-alerts"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "doctor"]}>
                            <EmergencyAlerts />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="admin/settings"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin"]}>
                            <Settings />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="admin/invitations"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin"]}>
                            <Invitations />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="admin/tenant-settings"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin"]}>
                            <TenantSettings />
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Doctor Pages */}
                      <Route
                        path="doctor/patient/:patientId"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "doctor"]}>
                            <PatientRecord />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="doctor/patient"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "doctor"]}>
                            <PatientRecord />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="doctor/prescriptions"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "doctor"]}>
                            <Prescriptions />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="doctor/case-review"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "doctor"]}>
                            <CaseReviewQueue />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="doctor/patient-queue"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "doctor"]}>
                            <PatientQueuePage />
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Nurse Pages */}
                      <Route
                        path="nurse/dashboard"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "nurse"]}>
                            <NurseDashboard />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="nurse/vitals"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "nurse"]}>
                            <VitalSigns />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="nurse/notes"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "nurse"]}>
                            <NurseNotes />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="nurse/patient/:patientId"
                        element={
                          <RoleProtectedRoute allowedRoles={["admin", "nurse"]}>
                            <PatientStatus />
                          </RoleProtectedRoute>
                        }
                      />
                    </Route>
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
              </TenantProvider>
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
